/* eslint-disable @typescript-eslint/no-unused-vars */
import { Box, Button, Card, CardHeader, FormControl, IconButton, Link, Menu, MenuItem, Select, Tooltip } from "@mui/material";
import { Issue } from "../../data/Issue";
import { getIssuePriorityNameRu, IssuePriority } from "../../data/IssuePriority";
import FlagIcon from '@mui/icons-material/Flag';
import moment from 'moment';
import { useEffect, useState } from "react";
import { useRootStore } from "../../hooks/useRootStore";
import { getIssueStateNameRu, IssueState } from "../../data/IssueState";
import AcceptIssueDialog from "../AcceptIssueDialog";
import PauseIssueDialog from "../PauseIssueDialog";
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import ChangeEstimatedDateDialog from "../ChangeEstimatedDateDialog";
import IssueResultDialog from "../IssueResultDialog";
import { UserType } from "../../data/UserType";
import ChangeProjectDialog from "../ChangeProjectDialog";
import { Project } from "../../data/Project";
import ChangeSkillDialog from "../ChangeSkillDialog";
import { Skill } from "../../data/Skill";
import { CustomerEmployeeProfile, EmployeeProfile } from "../../data/UserProfile";
import ResumePausedIssueDialog from "../ResumePausedIssueDialog";
import { EmployeeAccessLevel } from "../../data/EmployeeAccessLevel";
import { CustomerEmployeeAccessLevel } from "../../data/CustomerEmployeeAccessLevel";
import { IssueScore } from "../../data/IssueScore";
import RateIssueDialog from "../RateIssueDialog";
import { observer } from "mobx-react-lite";
import RejectErrorDialog from "../RejectErrorDialog";
import ChangePriorityDialog from "../ChangePriorityDialog";
import { Employee } from "../../data/Employee";
import RejectIssueDialog from "../RejectIssueDialog";
import './IssueMainWidget.css';
import ConfirmDialog from "../ConfirmDialog";

const IssueMainWidget = observer(({ issue, onIssueChanged }: { issue: Issue, onIssueChanged: (changed: Issue) => void }) => {
    const { api, userProfile, globalError, activityStarted, setUserProfile } = useRootStore();
    const [changed, setChanged] = useState(issue);

    const [rejectErrorDialogOpen, setRejectErrorDialogOpen] = useState(false);
    const [rejectIssueDialogOpen, setRejectIssueDialogOpen] = useState(false);
    const [acceptIssueDialogOpen, setAcceptIssueDialogOpen] = useState(false);
    const [pauseIssueDialogOpen, setPauseIssueDialogOpen] = useState(false);
    const [resumeIssueDialogOpen, setResumeIssueDialogOpen] = useState(false);
    const [priorityChanging, setPriorityChanging] = useState(false);
    const [estimatedDateChanging, setEstimatedDateChanging] = useState(issue.StateID === IssueState.stub);
    const [resultChanging, setResultChanging] = useState(false);
    const [projectIdChanging, setProjectIdChanging] = useState(false);
    const [skillIdChanging, setSkillIdChanging] = useState(false);
    const [rateChanging, setRateChanging] = useState(false);
    const [archiveIssueDialogOpen, setArchiveIssueDialogOpen] = useState(false);
    const [workerEditing, setWorkerEditing] = useState(false);
    const [possibleWorkers, setPossibleWorkers] = useState([] as Employee[]);
    const [skills, setSkills] = useState<Skill[]>([]);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const acceptMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const updateIssue = (changed: Issue) => {
        setChanged(changed);
        onIssueChanged(changed);
    }

    const assignIssueToMe = () => {
        if (userProfile && userProfile.UserType == UserType.Employee) {
            const profile = { ...userProfile } as EmployeeProfile;
            api.assignIssue(changed.ID, profile.ID).then(() => {
                api.getIssue(issue.ID).then(i => {
                    if (i) {
                        updateIssue(i);
                    }
                    setAcceptIssueDialogOpen(false);
                });
            })
        }
        else {
            throw new Error('вы не авторизованы');
        }
    }

    const queueIssue = () => {
        api.queueIssue(changed.ID).then(() => {
            api.getIssue(issue.ID).then(i => {
                if (i) {
                    updateIssue(i);
                }
            });
        })
    }

    useEffect(() => {
        api.getIssuePossibleEmloyee(issue.ID).then(possibleWorkers => {
            if (issue.Project?.NoQueueScheduler && [IssueState.assigned, IssueState.backlog, IssueState.queued, IssueState.stub].includes(issue.StateID)) {
                setPossibleWorkers(possibleWorkers)
            }
        });

        if (issue.Project?.NoQueueScheduler) {
            api.getSkills().then(skills => setSkills(skills));
        }
    }, [issue, api]);

    const testIssue = async () => {
        await api.testIssue(changed.ID).then(() => {
            api.getIssue(issue.ID).then(i => {
                if (i) {
                    updateIssue(i);
                }
            });
        })
    }

    const archiveIssue = async () => {
        await api.archiveIssue(changed.ID).then(() => {
            setArchiveIssueDialogOpen(false);
            api.getIssue(issue.ID).then(i => {
                if (i) {
                    updateIssue(i);
                }
            });
        })
    }

    const handoverIssueToClient = async () => {
        await api.handoverIssueToClient(changed.ID).then(() => {
            api.getIssue(issue.ID).then(i => {
                if (i) {
                    updateIssue(i);
                    const profile = { ...userProfile };
                    if (profile?.UserType == UserType.Employee) {
                        (profile as EmployeeProfile).ActiveIssueId = null;
                        setUserProfile(profile as EmployeeProfile);
                    }
                }
            });
        })
    }

    const AssignIssue = (workerId: number): void => {
        api.assignIssue(issue.ID, workerId).then(() => {
            setWorkerEditing(false);
            api.getIssue(issue.ID).then(i => {
                if (i) {
                    updateIssue(i);
                }
            });
        })
    }

    const ChangeIssueSkillId = (skillId: number): void => {
        api.updateIssueSkillID(issue.ID, skillId!, [])
            .then(() => {
                setSkillIdChanging(false);
                updateIssue({ ...changed, RequiredSkillID: skillId, RequiredSkillName: skills.filter(x => x.ID == skillId)[0].Name });
            })
    };

    const onIssueAccept = async (_estimatedHours: number) => {
        api.getIssue(issue.ID).then(i => {
            if (i) {
                updateIssue(i);
                const profile = { ...userProfile };
                if (profile?.UserType == UserType.Employee) {
                    (profile as EmployeeProfile).ActiveIssueId = i!.ID;
                    setUserProfile(profile as EmployeeProfile);
                }
            }
            setAcceptIssueDialogOpen(false);
        });
    }

    const onIssuePaused = async () => {
        api.getIssue(issue.ID).then(i => {
            if (i) {
                updateIssue(i);
                const profile = { ...userProfile };
                if (profile?.UserType == UserType.Employee) {
                    (profile as EmployeeProfile).ActiveIssueId = null;
                    setUserProfile(profile as EmployeeProfile);
                }
            }
            setPauseIssueDialogOpen(false);
        });
    }

    const onIssueCompleted = async () => {
        api.getIssue(issue.ID).then(i => {
            if (i) {
                updateIssue(i);
            }
            setRateChanging(false);
        });
    }

    const onIssueResumed = async () => {
        api.getIssue(issue.ID).then(i => {
            if (i) {
                updateIssue(i);
            }
            setResumeIssueDialogOpen(false);
        });
    }

    const onIssueErrorRejected = async (_rejectReason: string) => {
        api.getIssue(changed.ID).then((issue) => {
            updateIssue(issue!);
        })
        setRejectErrorDialogOpen(false);
    }

    const onIssueRejected = async (_rejectReason: string) => {
        api.getIssue(changed.ID).then((issue) => {
            updateIssue(issue!);
        })
        setRejectIssueDialogOpen(false);
    }

    const onIssueProjectChanged = (_project: Project) => {
        api.getIssue(issue.ID).then(i => {
            if (i) {
                updateIssue(i);
            }
            setProjectIdChanging(false);
        });
    };

    const tryPauseIssue = () => setPauseIssueDialogOpen(true);
    const tryResumeIssue = () => setResumeIssueDialogOpen(true);
    const tryCompleteIssue = () => setRateChanging(true);
    const tryAcceptIssue = () => setAcceptIssueDialogOpen(true);
    const tryRejectError = () => setRejectErrorDialogOpen(true);
    const tryRejectIssue = () => setRejectIssueDialogOpen(true);
    const tryArchiveIssue = () => setArchiveIssueDialogOpen(true);

    const checkCanArchive = () => {
        return changed.AuthorID == userProfile?.ID
            || (userProfile?.UserType == UserType.Employee
                && (userProfile! as EmployeeProfile).AccessLevelID == EmployeeAccessLevel.fullAccess)
            || (userProfile?.UserType == UserType.CustomerEmployee
                && (userProfile! as CustomerEmployeeProfile).AccessLevelID == CustomerEmployeeAccessLevel.fullAccess
                && (userProfile! as CustomerEmployeeProfile).CustomerID == changed.ClientID);
    };

    class IssueCommand {
        Name: string;
        Action: () => void;

        constructor(name: string, action: () => void) {
            this.Name = name;
            this.Action = action;
        }

        Execute() {
            this.Action();
            setAnchorEl(null);
        }
    }

    const canChangePriority = () => {
        return [
            IssueState.stub,
            IssueState.backlog,
            IssueState.assigned,
            IssueState.queued
        ].includes(changed?.StateID)
            && changed.AuthorID == userProfile?.ID;
    }

    const getIssueAvailableCommands = () => {

        const commands = [] as IssueCommand[];

        /*if (activityStarted && (userProfile as EmployeeProfile).ActiveIssueId == issue.ID) {
            return commands;
        }*/

        switch (changed?.StateID) {

            case IssueState.backlog:
                commands.push(new IssueCommand('Поставить в очередь', queueIssue));

                if (checkCanArchive()) {
                    commands.push(new IssueCommand('В архив', tryArchiveIssue));
                }

                break;

            case IssueState.queued:

                if (checkCanArchive()) {
                    commands.push(new IssueCommand('В архив', tryArchiveIssue));
                }
                if (changed.Project?.NoQueueScheduler && !(userProfile as EmployeeProfile).ActiveIssueId) {
                    commands.push(new IssueCommand('Взять себе', assignIssueToMe));
                }
                break;

            case IssueState.assigned:
                if (userProfile?.UserType == UserType.Employee && changed.AssignedToEmployeeAgentID == userProfile.ID) {
                    commands.push(new IssueCommand('Отказаться', tryRejectIssue));
                    if (changed.IsError) {
                        commands.push(new IssueCommand('Это не ошибка', tryRejectError));
                    }

                    if (!(userProfile as EmployeeProfile).ActiveIssueId) {
                        commands.push(new IssueCommand('Взять в работу', tryAcceptIssue));
                    }
                }
                if (checkCanArchive()) {
                    commands.push(new IssueCommand('В архив', tryArchiveIssue));
                }
                break;

            case IssueState.accepted:
                if (userProfile?.UserType == UserType.Employee
                    && (userProfile as EmployeeProfile).ActiveIssueId == issue.ID
                    && !activityStarted) {
                    commands.push(new IssueCommand('Нужны комментарии', tryPauseIssue));
                    commands.push(new IssueCommand('В тестирование', testIssue));
                }

                break;

            case IssueState.testing:
                if (userProfile?.UserType == UserType.Employee
                    && (userProfile as EmployeeProfile).ActiveIssueId == issue.ID) {
                    commands.push(new IssueCommand('Нужны комментарии', tryPauseIssue));
                    commands.push(new IssueCommand('Вернуть в работу', tryAcceptIssue));
                    commands.push(new IssueCommand('Показать клиенту', handoverIssueToClient));
                }

                break;

            case IssueState.commentsWaiting:
                if (
                    (userProfile!.UserType == UserType.Employee
                        && (userProfile as EmployeeProfile).AccessLevelID == EmployeeAccessLevel.fullAccess)
                    ||
                    (userProfile!.UserType == UserType.CustomerEmployee
                        && (userProfile as CustomerEmployeeProfile).AccessLevelID == CustomerEmployeeAccessLevel.fullAccess)
                    ||
                    (userProfile!.ID == issue.AssignedToEmployeeAgentID)
                    ||
                    (userProfile!.ID == issue.AuthorID)) {
                    commands.push(new IssueCommand('Возобновить', tryResumeIssue));
                }
                break;

            //case IssueState.criticalWaiting:

            case IssueState.available:
                if (
                    (userProfile?.UserType == UserType.CustomerEmployee
                        && (userProfile as CustomerEmployeeProfile).AccessLevelID == CustomerEmployeeAccessLevel.fullAccess)
                    ||
                    (userProfile?.ID == issue.AuthorID))
                {
                    commands.push(new IssueCommand('Завершить', tryCompleteIssue));
                    if (changed.Project?.NoQueueScheduler) {
                        commands.push(new IssueCommand('Вернуть в работу', () => AssignIssue(issue.AssignedToEmployeeAgentID)));
                    }
                }
                break;
        }

        return commands;
    }

    const availableCommands = getIssueAvailableCommands();

    return (
        <>
            {priorityChanging ? <ChangePriorityDialog issueId={issue.ID} initialPriorityID={changed.PriorityID as IssuePriority} initialIsError={changed.IsError} onSubmit={(priorityId: IssuePriority, isError: boolean) => { setChanged({ ...changed, PriorityID: priorityId, IsError: isError }); setPriorityChanging(false); }} onCancel={() => { globalError.reset(); setPriorityChanging(false); }} /> : ''}
            {rejectErrorDialogOpen ? <RejectErrorDialog issueId={issue.ID} estimatedHours={changed.EstimationHours} onSubmit={onIssueErrorRejected} onCancel={() => { globalError.reset(); setRejectErrorDialogOpen(false); }} /> : ''}
            {rejectIssueDialogOpen ? <RejectIssueDialog issueId={issue.ID} onSubmit={onIssueRejected} onCancel={() => { globalError.reset(); setRejectIssueDialogOpen(false); }} /> : ''}
            {acceptIssueDialogOpen ? <AcceptIssueDialog issueId={issue.ID} initialEstimatedHours={changed.EstimationHours} onSubmit={onIssueAccept} onCancel={() => { globalError.reset(); setAcceptIssueDialogOpen(false); }} /> : ''}
            {pauseIssueDialogOpen ? <PauseIssueDialog issueId={issue.ID} onSubmit={onIssuePaused} onCancel={() => { globalError.reset(); setPauseIssueDialogOpen(false); }} /> : ''}
            {resumeIssueDialogOpen ? <ResumePausedIssueDialog issueId={issue.ID} onSubmit={onIssueResumed} onCancel={() => { globalError.reset(); setResumeIssueDialogOpen(false); }} /> : ''}            
            {estimatedDateChanging ? <ChangeEstimatedDateDialog issueId={issue.ID} oldDate={changed.EstimatedDate} onSubmit={(newDate: Date | null) => { setChanged({ ...changed, EstimatedDate: newDate }); setEstimatedDateChanging(false); }} onCancel={() => { globalError.reset(); setEstimatedDateChanging(false); }} /> : ''}
            {resultChanging ? <IssueResultDialog assignedEmployeeId={issue.AssignedToEmployeeAgentID} issueId={issue.ID} result={changed.Result} onSubmit={(result: string) => { setChanged({ ...changed, Result: result }); setResultChanging(false); }} onCancel={() => { globalError.reset(); setResultChanging(false); }} /> : ''}
            {projectIdChanging ? <ChangeProjectDialog issueId={issue.ID} project={changed.Project} onSubmit={(project: Project) => onIssueProjectChanged(project)} onCancel={() => { globalError.reset(); setProjectIdChanging(false); }} /> : ''}
            {archiveIssueDialogOpen && <ConfirmDialog text='Убрать задачу в архив?' onConfirm={() => archiveIssue()} onCancel={() => setArchiveIssueDialogOpen(false)} />}
            {(skillIdChanging && issue.Project?.NoQueueScheduler === false) ? <ChangeSkillDialog issue={issue} onSubmit={(changedSkill: Skill/*, preferredWorkers: Employee[]*/) => { setChanged({ ...changed, RequiredSkillID: changedSkill.ID, RequiredSkillName: changedSkill.Name }); setSkillIdChanging(false); }} onCancel={() => { globalError.reset(); setSkillIdChanging(false); }} /> : ''}
            {rateChanging ? <RateIssueDialog issueId={issue.ID} onSubmit={(_score: IssueScore) => onIssueCompleted()} onCancel={() => { globalError.reset(); setRateChanging(false); }} /> : ''}
            <Card className="issue-widget main" sx={{position: 'relative'}}>
                <Box onClick={() => { if (canChangePriority()) { setPriorityChanging(true) } }} className={`priority priority-${changed.PriorityID}`}>
                    {changed.IsError
                        ? <span style={{ color: 'red' }}>ошибка</span>
                        : getIssuePriorityNameRu(changed.PriorityID) ?? changed.PriorityID}
                    <FlagIcon />
                </Box>
                <CardHeader
                    subheader={moment(changed.CreationDate).format('MMMM DD, YYYY')}
                />
                <Box className="issue-widget-editable-value">
                    Статус: {changed.StateID ? getIssueStateNameRu(changed?.StateID) : 'не определено'}                    
                    {availableCommands.length === 0
                        ? '' :
                        <>
                            <IconButton
                                aria-label="more"
                                disableFocusRipple={true}
                                disableRipple={true}
                                size='small'
                                sx={{ padding: 0, ml: 1 }}
                                id="state-change-button"
                                aria-controls={open ? 'basic-menu' : undefined}
                                aria-expanded={open ? 'true' : undefined}
                                aria-haspopup="true"
                                onClick={acceptMenuClick}
                            >
                                <MoreHorizIcon />
                            </IconButton>
                            <Menu
                                id="basic-menu"
                                anchorEl={anchorEl}
                                onClose={() => setAnchorEl(null)}
                                open={open}
                                MenuListProps={{
                                    'aria-labelledby': 'basic-button',
                                }}
                            >
                                {getIssueAvailableCommands().map(command =>
                                    <MenuItem key={command.Name} onClick={() => command.Execute()}>{command.Name}</MenuItem>
                                )}
                            </Menu>
                        </>
                    }
                </Box>
                {changed.StateID != IssueState.completed ? '' :
                    <Box>Оценка: {changed.ScoreID}/10</Box>
                }
                <Box>Заявитель: <Link href={`./employees/${changed.AuthorID}`} title="Открыть карточку сотрудника">{changed.AuthorName}</Link></Box>
                <Box>
                    <Box className="issue-widget-editable-value">
                        <Box>
                            Дедлайн: {changed.EstimatedDate ? moment(changed.EstimatedDate).format('DD.MM.YYYY') : ''}
                        </Box>
                        {[IssueState.accepted, IssueState.testing, IssueState.commentsWaiting, IssueState.criticalWaiting, IssueState].includes(issue.StateID)
                            ?
                            <IconButton
                                className="edit-btn"
                                sx={{ p: 0, }}
                                onClick={() => setEstimatedDateChanging(!estimatedDateChanging)} title={estimatedDateChanging ? 'Отменить' : 'Изменить запланированную дату'}
                            >
                                <ModeEditIcon />
                            </IconButton>
                            :
                            ''
                        }
                    </Box>
                </Box>
                <Box>
                    <Box className="issue-widget-editable-value">
                        <Box>
                            Клиент: {changed.Project ? <Link href={`./clients/${changed.Project.ClientID}`} title="Открыть карточку клиента">{changed.Project.ClientName}</Link> : ''}
                        </Box>
                        {[IssueState.assigned, IssueState.backlog, IssueState.queued, IssueState.stub].includes(changed.StateID)
                            ?
                            <IconButton
                                className="edit-btn"
                                sx={{ p: 0, }}
                                onClick={() => setProjectIdChanging(true)}
                                title='Изменить клиента'
                            >
                                <ModeEditIcon />
                            </IconButton>
                            : ''
                        }
                    </Box>
                </Box>
                <Box>
                    <Box className="issue-widget-editable-value">
                        <Box>
                            Проект: {changed.Project ? <Link href={`./projects/${changed.Project.ID}`} title="Открыть карточку проекта">{changed.Project.Name}</Link> : ''}
                        </Box>
                        {[IssueState.assigned, IssueState.backlog, IssueState.queued, IssueState.stub].includes(changed.StateID)
                            ?
                            <IconButton
                                className="edit-btn"
                                sx={{ p: 0, }}
                                onClick={() => setProjectIdChanging(true)}
                                title='Изменить проект'
                            >
                                <ModeEditIcon />
                            </IconButton>
                            : ''
                        }
                    </Box>
                </Box>
                <Box>
                    <Box className="issue-widget-editable-value">
                        <Box>
                            <span>Направление:&nbsp;</span>{skillIdChanging && issue.Project?.NoQueueScheduler ? '' : changed.RequiredSkillName}
                        </Box>
                        {[IssueState.assigned, IssueState.backlog, IssueState.queued, IssueState.stub].includes(changed.StateID)
                            ?
                            <IconButton
                                className="edit-btn"
                                sx={{ p: 0, }}
                                onClick={() => setSkillIdChanging(!skillIdChanging)}
                                title={skillIdChanging ? 'Отменить' : 'Изменить направление'}
                            >
                                <ModeEditIcon />
                            </IconButton>
                            : ''
                        }
                    </Box>
                    {skillIdChanging && issue.Project?.NoQueueScheduler
                        ?
                        <FormControl sx={{ mt: 1, width: "100%", minWidth: 120 }}>
                            <Select
                                id="demo-simple-select-disabled"
                                defaultValue={issue?.RequiredSkillID ? issue?.RequiredSkillID : ''}
                                onChange={(e) => ChangeIssueSkillId(e.target.value as number)}
                            >
                                {skills.map((w) => <MenuItem key={w.ID} value={w.ID}>{w.Name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        : ''
                    }
                </Box>
                {changed.StateID == IssueState.available ?
                    <Box>
                        <Tooltip placement="left" arrow title="Задача будет автоматически закрыта в указанную дату"><span style={{ paddingRight: '5px' }}>Автозакрытие:</span></Tooltip> {changed.AcceptanceDate ? moment(changed.AcceptanceDate).format('DD.MM.YYYY') : ''}
                    </Box>
                    : ''
                }
                <Box>
                    <Box className="issue-widget-editable-value">
                        <Box>
                            <span>Исполнитель:&nbsp;</span>{!workerEditing ?
                                issue?.AssignedToEmployeeAgentID
                                    ? <Link href={`./employees/${issue?.AssignedToEmployeeAgentID}`} title="Открыть карточку сотрудника">{issue?.AssignedToEmployeeAgentName}</Link>
                                    : "не назначено"
                                : ''
                            }
                        </Box>

                        {issue
                            && issue.Project?.NoQueueScheduler
                            && possibleWorkers.length > 0
                            && [IssueState.assigned, IssueState.backlog, IssueState.queued, IssueState.stub].includes(issue.StateID) ?
                            <IconButton className="edit-btn" sx={{ p: 0, }} onClick={() => setWorkerEditing(!workerEditing)} title={workerEditing ? 'Отменить' : 'Изменить исполнителя'}>
                                <ModeEditIcon />
                            </IconButton>
                            : ''}
                    </Box>
                    {!workerEditing ? ''                    
                        :
                        <FormControl sx={{ mt: 1, width: "100%", minWidth: 120 }}>
                            <Select
                                id="demo-simple-select-disabled"
                                defaultValue={issue?.AssignedToEmployeeAgentID}
                                onChange={(e) => AssignIssue(e.target.value as number)}
                            >
                                {possibleWorkers.map((w) => <MenuItem key={w.ID} value={w.ID}>{w.Name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    }
                </Box>
                {(issue.PreferredWorkerBonusRate ?? 0) > 0 &&
                    <Box>
                        <span>Наценка:&nbsp;</span>{issue.PreferredWorkerBonusRate}%
                    </Box>
                }
                {((userProfile?.UserType == UserType.CustomerEmployee && (issue.Result ?? '').trim() != '')
                    || (userProfile?.UserType == UserType.Employee)) && [IssueState.accepted,
                        IssueState.available,
                        IssueState.testing,
                        IssueState.completed,
                        IssueState.commentsWaiting].includes(issue.StateID)
                    ?
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Button onClick={() => setResultChanging(true)}>Результат</Button>
                    </Box>
                    : ''
                }
            </Card>
        </>
    )
})

export default IssueMainWidget;
